import getCategoryDisplayName from '../config/category-display-names.js'

/**
 * Cluster tooltip module
 * Handles creation, positioning, and event management for cluster tooltips
 */

/**
 * Initialize cluster tooltip functionality
 * @param {Object} clusterGroup - Leaflet MarkerClusterGroup instance
 * @param {Object} map - Leaflet map instance
 * @param {string} mapType - Map type ('default' or 'volunteerGroups')
 * @param {Function} getFormattedFilename - Function to format category names for icon paths
 */
export function initClusterTooltip(clusterGroup, map, mapType, getFormattedFilename) {
  let clusterTooltip = null
  let clusterTooltipTimeout = null
  let isScrollingInTooltip = false
  let scrollTimeout = null

  /**
   * Create the tooltip DOM structure
   */
  const createClusterTooltip = (cluster) => {
    const markers = cluster.getAllChildMarkers()

    const tooltip = document.createElement('div')
    tooltip.classList.add('cluster-tooltip')

    const tooltipContent = document.createElement('div')
    tooltipContent.classList.add('cluster-tooltip__content')

    const tooltipList = document.createElement('ul')
    tooltipList.classList.add('cluster-tooltip__list')

    markers.forEach((marker) => {
      const category = marker.category || 'Overig'
      const displayName = getCategoryDisplayName(category)

      let iconUrl
      if (mapType == 'volunteerGroups') {
        // Use PNG for volunteer groups (standard), except Terreinen Brabants Landschap which uses SVG
        if (category === 'Terreinen Brabants Landschap') {
          iconUrl = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(category)}-icon.svg?v=1`
        } else {
          iconUrl = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(category)}-icon.png`
        }
      } else {
        iconUrl = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(category)}.svg?v=1`
      }

      const item = document.createElement('li')
      item.classList.add('cluster-tooltip__item')

      const itemContent = document.createElement('div')
      itemContent.classList.add('cluster-tooltip__item-content')

      const itemIcon = document.createElement('img')
      itemIcon.classList.add('cluster-tooltip__item-icon')
      itemIcon.src = iconUrl
      itemIcon.alt = displayName
      itemContent.appendChild(itemIcon)

      const itemText = document.createElement('div')
      itemText.classList.add('cluster-tooltip__item-text')

      const itemTitle = document.createElement('div')
      itemTitle.classList.add('cluster-tooltip__item-title')
      itemTitle.textContent = marker.name || 'Naamloos item'
      itemText.appendChild(itemTitle)

      const itemCategory = document.createElement('div')
      itemCategory.classList.add('cluster-tooltip__item-location')
      itemCategory.textContent = displayName
      itemText.appendChild(itemCategory)

      itemContent.appendChild(itemText)
      item.appendChild(itemContent)

      tooltipList.appendChild(item)
    })

    tooltipContent.appendChild(tooltipList)
    tooltip.appendChild(tooltipContent)

    return tooltip
  }

  /**
   * Update tooltip position relative to cluster and map bounds
   */
  const updateTooltipPosition = (cluster) => {
    if (!clusterTooltip || !cluster || !cluster._icon) return

    const icon = cluster._icon
    const rect = icon.getBoundingClientRect()
    const mapContainer = map.getContainer()
    const mapRect = mapContainer.getBoundingClientRect()

    const tooltipWidth = clusterTooltip.offsetWidth || 300
    const tooltipHeight = clusterTooltip.offsetHeight || 200

    const clusterCenterX = rect.left + rect.width / 2
    let left = clusterCenterX - tooltipWidth / 2
    let top = rect.top - tooltipHeight - 12

    if (left < mapRect.left) {
      left = mapRect.left + 10
    } else if (left + tooltipWidth > mapRect.right) {
      left = mapRect.right - tooltipWidth - 10
    }

    if (top < mapRect.top) {
      top = rect.bottom + 20
      clusterTooltip.classList.add('cluster-tooltip--below')
    } else {
      clusterTooltip.classList.remove('cluster-tooltip--below')
    }

    if (top + tooltipHeight > mapRect.bottom) {
      top = mapRect.bottom - tooltipHeight - 10
    }

    clusterTooltip.style.left = `${left}px`
    clusterTooltip.style.top = `${top}px`

    const arrowOffset = clusterCenterX - left
    clusterTooltip.style.setProperty('--arrow-offset', `${arrowOffset}px`)
  }

  /**
   * Clean up tooltip and all event listeners
   */
  const cleanupTooltip = () => {
    if (!clusterTooltip) return

    if (clusterTooltip._cleanup) {
      clusterTooltip._cleanup()
    }

    if (clusterTooltipTimeout) {
      clearTimeout(clusterTooltipTimeout)
      clusterTooltipTimeout = null
    }

    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
      scrollTimeout = null
    }

    clusterTooltip.remove()
    clusterTooltip = null
    isScrollingInTooltip = false
  }

  /**
   * Handle tooltip mouse enter
   */
  const handleTooltipMouseEnter = () => {
    if (clusterTooltipTimeout) {
      clearTimeout(clusterTooltipTimeout)
      clusterTooltipTimeout = null
    }
  }

  /**
   * Handle tooltip mouse leave
   */
  const handleTooltipMouseLeave = () => {
    if (isScrollingInTooltip) return

    clusterTooltipTimeout = setTimeout(() => {
      if (clusterTooltip && !isScrollingInTooltip) {
        cleanupTooltip()
      }
    }, 300)
  }

  /**
   * Handle scroll events in tooltip
   */
  const handleScrollEvent = (e) => {
    e.stopPropagation()
    isScrollingInTooltip = true
    clearTimeout(scrollTimeout)
    scrollTimeout = setTimeout(() => {
      isScrollingInTooltip = false
    }, 150)
  }

  /**
   * Handle wheel events in tooltip (with scroll boundary detection)
   */
  const handleWheel = (e) => {
    e.stopPropagation()
    handleScrollEvent(e)

    const tooltipContent = clusterTooltip?.querySelector('.cluster-tooltip__content')
    if (tooltipContent) {
      const isAtTop = tooltipContent.scrollTop <= 0
      const isAtBottom = tooltipContent.scrollTop >= tooltipContent.scrollHeight - tooltipContent.clientHeight - 1
      if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
        e.preventDefault()
      }
    }
  }

  /**
   * Setup event listeners for a tooltip
   */
  const setupTooltipEvents = (tooltip, cluster) => {
    const tooltipContent = tooltip.querySelector('.cluster-tooltip__content')

    // Store cluster reference for position updates
    tooltip._cluster = cluster

    // Mouse events
    tooltip.addEventListener('mouseenter', handleTooltipMouseEnter)
    tooltip.addEventListener('mouseleave', handleTooltipMouseLeave)

    // Scroll events
    tooltipContent.addEventListener('wheel', handleWheel, { passive: false })
    tooltipContent.addEventListener('touchmove', handleScrollEvent, { passive: false })
    tooltipContent.addEventListener('scroll', handleScrollEvent, true)

    // Map event listeners for position updates
    const updateOnMapChange = () => {
      if (tooltip) {
        updateTooltipPosition(cluster)
      }
    }

    const updateOnMapMoveEnd = () => {
      if (tooltip) {
        setTimeout(() => updateTooltipPosition(cluster), 50)
      }
    }

    map.on('move', updateOnMapChange)
    map.on('moveend', updateOnMapMoveEnd)
    map.on('zoom', updateOnMapChange)
    map.on('zoomend', updateOnMapMoveEnd)

    // Cleanup function
    tooltip._cleanup = () => {
      map.off('move', updateOnMapChange)
      map.off('moveend', updateOnMapMoveEnd)
      map.off('zoom', updateOnMapChange)
      map.off('zoomend', updateOnMapMoveEnd)

      tooltip.removeEventListener('mouseenter', handleTooltipMouseEnter)
      tooltip.removeEventListener('mouseleave', handleTooltipMouseLeave)

      tooltipContent.removeEventListener('wheel', handleWheel)
      tooltipContent.removeEventListener('touchmove', handleScrollEvent)
      tooltipContent.removeEventListener('scroll', handleScrollEvent, true)
    }
  }

  /**
   * Check if device is touch-enabled
   */
  const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
  }

  /**
   * Handle cluster mouse over
   */
  clusterGroup.on('clustermouseover', function (e) {
    // Don't show tooltip on touch devices
    if (isTouchDevice()) {
      return
    }

    const cluster = e.layer

    // Clear any existing timeout
    if (clusterTooltipTimeout) {
      clearTimeout(clusterTooltipTimeout)
      clusterTooltipTimeout = null
    }

    // Remove existing tooltip if any
    if (clusterTooltip) {
      cleanupTooltip()
    }

    // Create and show new tooltip
    clusterTooltip = createClusterTooltip(cluster)
    document.body.appendChild(clusterTooltip)

    // Setup events and position
    setupTooltipEvents(clusterTooltip, cluster)
    setTimeout(() => updateTooltipPosition(cluster), 10)
  })

  /**
   * Handle cluster mouse out
   */
  clusterGroup.on('clustermouseout', function (e) {
    if (isScrollingInTooltip) return

    // Use a longer delay to give time to move from cluster to tooltip
    // The tooltip's mouseenter handler will cancel this timeout if mouse reaches tooltip
    clusterTooltipTimeout = setTimeout(() => {
      if (clusterTooltip && !isScrollingInTooltip) {
        cleanupTooltip()
      }
    }, 300)
  })

  /**
   * Document-level handlers as backup for tooltip interaction
   */
  document.addEventListener(
    'mouseenter',
    function (e) {
      if (clusterTooltip && clusterTooltip.contains(e.target)) {
        if (clusterTooltipTimeout) {
          clearTimeout(clusterTooltipTimeout)
          clusterTooltipTimeout = null
        }
      }
    },
    true
  )

  document.addEventListener(
    'mouseleave',
    function (e) {
      if (clusterTooltip && clusterTooltip.contains(e.target)) {
        if (isScrollingInTooltip) return

        clusterTooltipTimeout = setTimeout(() => {
          if (clusterTooltip && !isScrollingInTooltip) {
            cleanupTooltip()
          }
        }, 300)
      }
    },
    true
  )
}
